// src/services/phoneHelpdeskApi.js
// --- UPDATE (Phone AI Helpdesk) client for /api/v1/phone-helpdesk
import api from './api';
import { config } from '../config';
import { getAuthToken } from '../utils/auth';

const BASE = '/phone-helpdesk';
const unwrap = (r) => (r && r.data && Object.prototype.hasOwnProperty.call(r.data, 'data') ? r.data.data : r?.data);

// Links into the rest of FlowSense. Override per deployment in .env if your routes differ.
export const ROUTES = {
  // paths from src/routes.js (layout /admin): /workorders, /road-works, /utilities, /helpdesk
  workOrder: process.env.REACT_APP_ROUTE_WORK_ORDER || '/admin/workorders?search=:id',
  roadIssue: process.env.REACT_APP_ROUTE_ROAD_ISSUE || '/admin/road-works?tab=public&issue=:id',
  road: process.env.REACT_APP_ROUTE_ROAD || '/admin/road-works?tab=report&road=:id',
  asset: process.env.REACT_APP_ROUTE_ASSET || '/admin/utilities?asset=:id',
  inspection: process.env.REACT_APP_ROUTE_ROAD_INSPECTION || '/admin/road-works?tab=report&road=:id',
  ticket: process.env.REACT_APP_ROUTE_TICKET || '/admin/helpdesk?ticket=:id&tab=trace',
};
export const routeTo = (kind, id) => (ROUTES[kind] ? ROUTES[kind].replace(':id', encodeURIComponent(id)) : '#');

export const CATEGORY_META = {
  pothole: { label: 'Pothole', color: 'orange', group: 'road' },
  road_damage: { label: 'Road damage', color: 'orange', group: 'road' },
  vehicle_damage: { label: 'Vehicle damage', color: 'red', group: 'road' },
  flooding: { label: 'Flooding', color: 'blue', group: 'flood' },
  drainage: { label: 'Blocked drain', color: 'cyan', group: 'flood' },
  streetlight: { label: 'Street light', color: 'yellow', group: 'other' },
  traffic_signal: { label: 'Traffic signal', color: 'purple', group: 'road' },
  debris: { label: 'Debris', color: 'gray', group: 'road' },
  sidewalk: { label: 'Sidewalk', color: 'teal', group: 'road' },
  other_complaint: { label: 'Complaint', color: 'pink', group: 'other' },
};
export const STAGE_LABEL = {
  received: 'Received', triaged: 'Triaged', routed: 'Routed', inspection_scheduled: 'Inspection scheduled',
  inspected: 'Inspected', work_ordered: 'Work order', in_repair: 'In repair', resolved: 'Resolved', closed: 'Closed', rejected: 'Rejected',
};
export const SEVERITY_COLOR = { low: 'green', medium: 'yellow', high: 'orange', critical: 'red' };

const phoneHelpdeskApi = {
  stats: (days = 30) => api.get(`${BASE}/stats`, { params: { days } }).then(unwrap),
  reports: (params = {}) => api.get(`${BASE}/reports`, { params }).then(unwrap),
  createReport: (body) => api.post(`${BASE}/reports`, body).then(unwrap),
  trace: (reportId) => api.get(`${BASE}/reports/${reportId}/trace`).then(unwrap),
  traceByTicket: (ticketId) => api.get(`${BASE}/tickets/${ticketId}/trace`).then(unwrap),
  floodContext: (reportId) => api.get(`${BASE}/reports/${reportId}/flood-context`).then(unwrap),
  verify: (reportId, body) => api.post(`${BASE}/reports/${reportId}/verify`, body).then(unwrap),
  scheduleInspection: (reportId, body = {}) => api.post(`${BASE}/reports/${reportId}/inspection`, body).then(unwrap),
  recordInspection: (reportId, body) => api.post(`${BASE}/reports/${reportId}/inspection/result`, body).then(unwrap),
  createWorkOrder: (reportId, body = {}) => api.post(`${BASE}/reports/${reportId}/work-order`, body).then(unwrap),
  resolve: (reportId, body) => api.post(`${BASE}/reports/${reportId}/resolve`, body).then(unwrap),
  attachRoad: (reportId, roadId) => api.post(`${BASE}/reports/${reportId}/attach-road`, { road_id: roadId }).then(unwrap),
  followUp: (reportId, note, nextHours) => api.post(`${BASE}/reports/${reportId}/follow-up`, { note, next_hours: nextHours }).then(unwrap),
  sendLocationLink: (reportId) => api.post(`${BASE}/reports/${reportId}/location-link`, {}).then(unwrap),
  clusters: (params = {}) => api.get(`${BASE}/clusters`, { params }).then(unwrap),
  recomputeClusters: () => api.post(`${BASE}/clusters/recompute`, {}).then(unwrap),
  map: (params = {}) => api.get(`${BASE}/map`, { params }).then(unwrap),
  roadFeed: (params = {}) => api.get(`${BASE}/road-feed`, { params }).then(unwrap),
  calls: (params = {}) => api.get(`${BASE}/calls`, { params }).then(unwrap),
  call: (callId) => api.get(`${BASE}/calls/${encodeURIComponent(callId)}`).then(unwrap),
  aiStatus: () => api.get(`${BASE}/ai/status`).then(unwrap),
  retrain: () => api.post(`${BASE}/ai/retrain`, {}).then(unwrap),
  classify: (text) => api.post(`${BASE}/ai/classify`, { text }).then(unwrap),
  runFollowUp: () => api.post(`${BASE}/follow-up/run`, {}).then(unwrap),

  /**
   * Token streaming from Ollama (SSE over fetch). onToken(t) is called for every token.
   * Returns { promise, abort }.
   */
  assistStream: (body, { onToken, onDone, onError } = {}) => streamSse(`${BASE}/assist/stream`, body, { onToken, onDone, onError }),
  replyStream: (ticketId, { onToken, onDone, onError } = {}) => streamSse(`/helpdesk/${ticketId}/generate-reply/stream`, {}, { onToken, onDone, onError }),
};

function streamSse(path, body, { onToken, onDone, onError }) {
  const ctl = new AbortController();
  const token = getAuthToken() || sessionStorage.getItem('token') || localStorage.getItem('token');
  const promise = (async () => {
    let full = '';
    try {
      const res = await fetch(`${config.apiUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body || {}),
        signal: ctl.signal,
      });
      if (!res.ok || !res.body) throw new Error(`Stream failed (HTTP ${res.status})`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) >= 0) {
          const block = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const ev = (block.match(/^event: (.*)$/m) || [])[1];
          const dataLine = (block.match(/^data: (.*)$/m) || [])[1];
          if (!dataLine) continue;
          let d;
          try { d = JSON.parse(dataLine); } catch (_) { continue; }
          if (ev === 'token' && d.t) { full += d.t; onToken && onToken(d.t, full); }
          else if (ev === 'done') { full = d.text || full; }
          else if (ev === 'error') throw new Error(d.message || 'AI error');
        }
      }
      onDone && onDone(full);
      return full;
    } catch (e) {
      if (e.name !== 'AbortError') onError && onError(e);
      return full;
    }
  })();
  return { promise, abort: () => ctl.abort() };
}

/** ws messages arrive as {type, payload}; older code passed the payload directly */
export const wsPayload = (msg) => (msg && msg.payload && typeof msg.payload === 'object' ? msg.payload : msg);

export default phoneHelpdeskApi;
// --- END UPDATE
